/**
 * «کیبورد آمد؛ فیلد کجا برود؟» — هندسه‌ی خالص، بدونِ react-native و بدونِ DOM
 * (WHOSUP-223).
 *
 * ═══ چرا این‌جاست و نه در هر اپ ═══
 *
 * دو اپ دو ابزارِ کاملاً متفاوت دارند: موبایل `Keyboard`ِ react-native و
 * `measureInWindow`، وب `visualViewport` و `getBoundingClientRect`. ولی
 * تصمیمی که بعد از اندازه‌گیری گرفته می‌شود در هر دو **یکی** است: «این
 * مستطیل زیرِ کیبورد مانده؟ چند پیکسل باید محتوا را جابه‌جا کرد؟»
 *
 * آن تصمیم چند حالتِ لبه دارد که هیچ‌کدام روی شبیه‌سازِ دمِ دست دیده نمی‌شوند
 * — فیلدی بلندتر از فضای باقی‌مانده، فیلدی که هم‌زمان زیرِ سربرگ و زیرِ
 * کیبورد است، کیبوردی که اصلاً روی این ناحیه نیفتاده. همان‌ها این‌جا با
 * `npm test` آزمودنی‌اند، و هر اپ فقط عدد می‌دهد و عدد می‌گیرد.
 */

/** مستطیلِ چیزی که باید دیده شود — فیلدی که تازه فوکوس گرفته. */
export type RevealBox = {
  top: number;
  bottom: number;
};

/** ناحیه‌ای که فیلد باید تویش دیده شود، و کیبوردی که از پایینش گرفته. */
export type RevealView = {
  /** بالای ناحیه — زیرِ سربرگِ چسبان، نه لبه‌ی صفحه. */
  top: number;
  /** پایینِ ناحیه، **پیش از** کیبورد. */
  bottom: number;
  /** چقدر از پایینِ همین ناحیه را کیبورد پوشانده. صفر یعنی نپوشانده. */
  keyboard: number;
  /** نفسی که بالای کیبورد و زیرِ سربرگ باقی بماند. */
  margin?: number;
};

/**
 * چقدر از پایینِ یک ناحیه زیرِ کیبورد مانده است.
 *
 * `viewBottom` و `keyboardTop` باید در یک دستگاهِ مختصات باشند: در موبایل
 * مختصاتِ پنجره (`measureInWindow` در برابرِ `endCoordinates.screenY`)، در وب
 * ویوپورتِ چیدمان (`innerHeight` در برابرِ `visualViewport.height + offsetTop`).
 *
 * عددِ منفی هیچ‌وقت برنمی‌گردد و همین مهم است: اگر پنجره **خودش** با آمدنِ
 * کیبورد کوچک شده باشد (حالتِ `adjustResize` اندروید، یا
 * `interactive-widget=resizes-content` در کروم) نتیجه صفر می‌شود و اپ دوباره
 * جا باز نمی‌کند. یعنی همین یک تابع جلوی «دو بار حساب کردنِ کیبورد» را
 * می‌گیرد — باگی که در آن حالت، فیلد را به‌اندازه‌ی یک کیبوردِ کامل از جایش
 * می‌پراند.
 */
export function keyboardOverlap(viewBottom: number, keyboardTop: number): number {
  if (!Number.isFinite(keyboardTop)) return 0;
  return Math.max(0, viewBottom - keyboardTop);
}

/**
 * چند پیکسل باید محتوا را بالا برد تا فیلد کاملاً دیده شود.
 *
 * مثبت یعنی «محتوا را بالا ببر» (اسکرول به پایین)، منفی یعنی عکسش، و صفر
 * یعنی دست نزن — که بیشترِ وقت‌ها همین است و عمدی است: هر جابه‌جاییِ لازم
 * نشده، همان «پرشِ ناگهانیِ چیدمان»ی است که نباید باشد.
 *
 * ترتیبِ اولویت‌ها هم عمدی است: **سرِ** فیلد مهم‌تر از تهش است. فیلدِ چندخطیِ
 * بلندتر از فضای باقی‌مانده اگر بخواهد کامل بالای کیبورد بیاید، خطِ اولش
 * می‌رود زیرِ سربرگ — یعنی جایی که مکان‌نما هست دیده نمی‌شود. پس تا جایی بالا
 * می‌رود که سرش هنوز پیدا باشد و نه بیشتر.
 */
export function revealShift(field: RevealBox, view: RevealView): number {
  const margin = view.margin ?? 0;
  const top = view.top + margin;
  const bottom = view.bottom - Math.max(0, view.keyboard) - margin;
  // ناحیه‌ای نمانده که چیزی را تویش نشان دهیم (کیبوردِ بزرگ روی صفحه‌ی کوتاه).
  if (bottom <= top) return 0;

  // سرِ فیلد زیرِ سربرگ است: بیاورش پایین، هرچقدر هم که تهش پایین باشد.
  if (field.top < top) return field.top - top;
  // ته‌اش زیرِ کیبورد است: ببرش بالا، ولی نه آن‌قدر که سرش برود زیرِ سربرگ.
  if (field.bottom > bottom) return Math.min(field.bottom - bottom, field.top - top);
  return 0;
}
